import {Discounts} from '../offer';
import {Brands} from '../../types';
import {Params} from '../http';
import {CheckoutPlan} from '../checkout';

/**
 * Adobe client layer Events
 */
export const HTTP_ERROR = 'HTTP_ERROR';
export const FORM_ERROR = 'FORM_ERROR';
export type AdobeDataLayerEventTypes = typeof HTTP_ERROR | typeof FORM_ERROR;
export const AdobeDataLayerEvent = new Set<AdobeDataLayerEventTypes>([HTTP_ERROR,FORM_ERROR]);

/**
 * Adobe client layer allows for pushing data and functions
 */
export interface AdobeClientDataLayer {
  push: PushDataLayerFunc;
  getState: ()=>GmUnified;
}

/**
 * Type defining data objects that can be pushed to data layer based on tagging plan
 */
export interface PushDataLayerFunc {
  (event: MakePartial<AdobePushData> | AdobePushFunc): void;
}

export interface GmUnified {
  _event: string; 
  _generalmotors: MakePartial<AdobePushData>;
}
/**
 * All types for pushing data to Adobe client data layer based on tagging plan
 */
export type AdobePushData = 
  PageInfo | VehicleInfo | VisitorProfile | FormInfo| ToolsInfo | Recalls |
  VehicleInfoUpdated | PageInfoUpdated | FormInfoUpdated | PlatformInfo | SessionInfo |
  CampaignInfo | GarageInfo | VehicleHistoryInfo | DealerInfo | ProductInfo | UserInfo |
  TransactionInfo | FeedbackInfo | NotificationInfo | ErrorLog | ErrorLog[] | ErrorLogs | LinkInfo | SearchInfo | EventInfo |
  Products | ErrorInfo | GmUnified;

/**
 * Adobe client data layer push accepts function types
 */
export type AdobePushFunc = (func: unknown) => void;

/**
 * Make all object properties optional
 */
export type MakePartial<T> = { [P in keyof T]?: MakePartial<T[P]> };

/**
 * @deprecated please use PageInfoUpdated
 */
export type PageInfo = {
  pageInfo: {
    pageName: string;
    siteSectionsLevel1: string;
    siteSectionsLevel2: string;
    siteSectionsLevel3: string;
    siteSectionsLevel4: string;
    siteSectionsLevel5: string;
    universalID: string; //TODO: not sure what this value should be
    url: string;
    seoStrategyPageName: string;
    pageType: string; //TODO
    pageSubType: string; //TODO
    languageSelected: string;
    siteName: string;
    brand: Brands;
    country: string;
    region: string;
    renderedExperience: string; //TODO
    viewport: string;
    orientation: string;
  };
};

export type Recalls = {
  recalls: {
    vin: string;
    type: string;
    ymm: string;
    count: number | string;
  };
};
/**
 * @deprecated please use VehicleInfoUpdated
 */
export type VehicleInfo = {
  vehicleInfo: {
    vehicleType: string;
    modelName: string;
    modelYear: string;
    bodyStyle: string;
    trim: unknown;
    trimLevel: string;
    condition: string;
    vin: string;
  };
};

export type AuthenticationState = 'logged in' | 'not logged in';
export const AUTH = 'logged in';
export const UNAUTH = 'not logged in';

export type VisitorProfile = {
  visitorProfile: {
    userProfileStateAuthentication: AuthenticationState;
    userStateProvince:string;
    userLoginStart: string;
    userLoginComplete: string;
    userRegistrationStart: string;
    userRegistrationComplete: string;
    zipCode: string;
    garageVehicleBrand: string;
    garageVehicleModelName: string;
    garageVehicleModelYear: string;
    garageVehiclesCount: number;
    gmEnergyEnrollmentType?:string;
    onStarStatus:string;
    activeOnstarProduct:string[];
  };
};
/**
 * @deprecated please use FormInfoUpdated
 */
export type FormInfo = {
  formInfo: {
    formName: string;
    formFieldName: string;
    errorMessage: string;
    errorType: string;
  };
};

type ToolsInfo = {
  toolsInfo: {
    formSteps: string;
    dealerName: string;
    dealerId: string;
    formErrors: string;
  };
};

//Adding new versions here so we can use in both services
export type UserInfo = {
  user: {
      gmEnergyEnrollmentType:string;
      ecid: string;
      id: string;
      globalProfileId: string;
      ownerProfileId: string;
      gcin: string;
      visitorId: string;
      userId:string;
      universalId: string;
      gcinId: string;
      onstarAccountId: string;
      channel:string;
      authentication:{
          state: string,
          method:string
      };
      accountStartDate: string;
      fullName: string;
      emailAddress: string;
      dateOfBirth: string;
      preferredLanguage: string;
      country: string;
      region:string;
      accountCountry?:string;
      accountRegion:string;
      zipCode:string;
      gender:string;
      preferredDealer:{
          id:string,
          name:string,
          zipCode:string
      };
      businessAccountStatus:string;
      lineOfBusiness:string;
      organization:{
          id:string,
          name:string,
      },
      onboardingStatus:string;
      registrationMethod:string;
      customerTenure:string;
      rewardsInfo: {
          memberId:string,
          tier:string,
          cardholderStatus:string,
          gmCardholderStatus:string,
          enrollmentStatus:string,
          rewardsStatus:string
      }
  };
};
export type PlatformInfo = {
  platform: {
      id: string;
      name:string;
      type: string;
      version: string;
      deploymentDate: string;
      environmentType:string;
      supportedMarket:string;
  }
  
}
export type SessionInfo = {
  session:{
      source: string;
      referrer: string;
  }
  
}
export type PageInfoUpdated = {
  page:{
      pageName: string;
      type: string;
      subType: string;
      pageGroup1: string;
      pageGroup2: string;
      pageGroup3: string;
      pageGroup4: string;
      pageGroup5: string;
      language: string;
      country: string;
      region: string;
      brand: string;
      scrollDepth: string;
      title: string;
      siteName: string;
      params:Params
      previousPage: string;
      referrer: string;
      url: string;
      path:string;
  }
}
export type CampaignInfo = {
  id: string;
  name: string;
  type: string;
  brand: string;
  audience: string;
}

export type VehicleInfoUpdated = {
  vehicle: {
      id: string;
      vin:string;
      brand: string;
      modelName: string;
      bodyStyle: string;
      color: unknown;
      modelYear: string;
      trim: unknown;
      type: unknown;
      driveType: string;
      powerTrain: string;
      condition: string;
      status: string;
      location: string;
      ownershipStatus: string;
      tenure: string;
      purchaseDate: string;
      leaseEndDate: string;
      mileage: string;
      price: string;
      resaleValue:string;
      drpo: string;
      promotions: PromotionsFields[];
      packages:PackageFields[];
      features:FeatureFields[];
      history:VehicleHistoryInfo;
      }
  };
export type GarageInfo = {
  garage:{
      id:string;
      chargers:{
          type:string[];
          brand:string[];
          status:string[];
      },
      vehicleList:string[];
      vehicleCount:number | null;
      sharedVehicleList:string[];
      sharedVehicleCount:number | null;
  }
}
export type VehicleHistoryInfo = 
  {
    id:string;
    event:string;
    description:string;
    recordedDate:Date;
    serviceEntity:string;
    location:string;
    serviceCost:number | null;
    mileageAtTime:number | null;
    partsChanged:string[];
    warrantyStatus:string;
    serviceReceipt:string;
    relatedDocuemnts:unknown[];
    notes:string;
  };

export type DealerInfo = {
  dealer:{
      id:string;
      name:string;
      group:string;
      brand:string;
      region:string;
      country:string;
      state:string;
      dma:string;
      city:string;
      zipCode:string;
  };
}
export type ProductInfo = {
  product:{
      id:string;
      name:string;
      category:string;
      price:number | null;
      listPrice:number | null;
      displayprice:number | null;
      brand:string;
      salesChannel:string;
      variantId:string;
      variant:string;
      discounts:{
          type:string;
          name:string;
          amount: number | string | null; //TODO: update with new process when available
      }
  };
}
export type TransactionInfo = {
  transaction:{
      id:string;
      totalAmount:number| null;
      subTotal:number | null;
      shipping:number| null;
      tax:number| null;
      brand:string;
      salesChannel:string;
      rewardsPoints:number | null;
      discount:Array<DiscountInfo>;
      productsList:Array<CheckoutPlan>;
      campaignsAssociated:Array<CampaignInfo>;
  }
  
}
export type FormInfoUpdated = {
  form: FormObject;
}
export type FormObject ={
  id:string;
  name:string;
  fieldName:string;
  interactionType:string;
  location:string;
}
export type FeedbackInfo = {
  feedback:{
      id:string;
      type:string;
      source:number | null;
      content:string;
      submissionDate:string;
      npsScore:number | null;
      csatScore:number | null;
      cesScore:number | null;
      fcr:boolean;
      fcrTimes:number | null;
      emotion:string;
  }

}
export type NotificationInfo = {
  notification: Notification
}

export type Notification = {
  id:string;
  channel:string;
  category:string;
  title:string;
  content:string;
  brand:string;
  readStatus:boolean;
  deliveryStatus:string;
}

export type ErrorLog = {
  errorLog:ErrorInfo
}

export type ErrorLogs = {
  errorLog: ErrorLog[];
}

export type ErrorInfo = {
  errorId:string;
  errorType:string;
  errorCode:string;
  errorMessage:string;
  errorLocation:string;
  integrationId:string;
  platformId:string;
}
export type LinkInfo = {
  link:{
      text:string;
      destination:string;
      type:string;
      interactionType:string;
      location:string;
  }
}
export type SearchInfo = {
  search:{
      term:string;
      resultCount:number | null;
      resultClicked:string;
      facetsUsed:string[];
      filtersUsed:string[];
  }
}
export type EventInfo = {
  eventType: {
    type: string;
    location: string;
  };
};
export type PackageFields = {
  name: string;
  status: string;
  duration: string;
  plan: string;
};
export type FeatureFields = {
  name: string;
  capable: boolean;
  enrolled: boolean;
};
export type PromotionsFields = {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  description: string;
};

export interface Products {
  products: DataProduct[];
}

export interface DataProduct {
  productId: string;
  productName: string;
  productCategory: string;
  price: number | null;
  quantity: number | null;
  listPrice: number | null;
  displayPrice: number | null;
  productBrand: string;
  productVariantId: string;
  productVariant: string;
  discounts: DiscountInfo;
}

export type DiscountInfo = {
  amount: number | string | null;
  discounts: Array<Discounts>;
}
